import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { ArrowRight, FileText, TrendingDown, Calculator as CalcIcon, CheckCircle2, AlertTriangle } from 'lucide-react';

const formatCurrency = (amount) =>
  new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount || 0);

const formatMonths = (months) => {
  const m = Math.max(0, Math.round(months));
  const years = Math.floor(m / 12);
  const remaining = m % 12;
  if (years === 0) return `${remaining} mo`;
  if (remaining === 0) return `${years} yr`;
  return `${years} yr ${remaining} mo`;
};

// Per-debt monthly payment (annuity formula)
const debtMonthly = (debt) => {
  const r = (Number(debt.rate) || 0) / 100 / 12;
  const n = (Number(debt.term) || 0) * 12;
  const p = Number(debt.amount) || 0;
  if (!p || !n) return 0;
  if (!r) return p / n;
  return (p * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
};

const debtTotalInterest = (debt) => {
  const monthly = debtMonthly(debt);
  const months = (Number(debt.term) || 0) * 12;
  return monthly * months - (Number(debt.amount) || 0);
};

/**
 * ProposalBreakdown — itemised "what we're proposing" + side-by-side savings comparison.
 *
 * Props:
 *   - results: output of calculateDebtConsolidation
 *   - debts: input debt array (name, amount, rate, term)
 *   - mortgage: { remainingBalance, interestRate, remainingTerm }
 *   - consolidationRate: number (percent)
 */
const ProposalBreakdown = ({ results, debts, mortgage, consolidationRate }) => {
  if (!results) return null;

  const currentTotalDebtMonthly = debts.reduce((s, d) => s + debtMonthly(d), 0);
  const currentTotalDebtInterest = debts.reduce((s, d) => s + debtTotalInterest(d), 0);
  const currentMonthly = results.current.totalMonthlyPayment;
  const proposedMonthly = results.consolidated.monthlyPayment;
  const redrawMonthly = results.redrawStrategy.monthlyPayment;

  const monthlySavings = results.consolidated.monthlySavings;
  const totalSavings = results.redrawStrategy.totalSavings;
  const yearsSaved = results.redrawStrategy.yearsSaved;
  const remainingMonths = (mortgage.remainingTerm || 0) * 12;

  return (
    <div className="mt-12 space-y-8" data-testid="proposal-breakdown">
      <div className="text-center">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-indigo-100 text-indigo-700 text-xs font-semibold uppercase tracking-wide mb-3">
          <FileText className="h-3.5 w-3.5" /> Proposal &amp; Reduction Report
        </div>
        <h2 className="text-3xl font-bold text-gray-900">What We're Proposing</h2>
        <p className="text-gray-600 mt-1">A line-by-line breakdown of the consolidation strategy and what it reduces.</p>
        <p className="text-sm text-gray-500 mt-2 italic">Scroll down to "Send Live Proposal" to share this breakdown live with your client.</p>
      </div>

      {/* ============== 1. PROPOSAL BREAKDOWN ============== */}
      <Card className="border-2 border-indigo-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CalcIcon className="h-5 w-5 text-indigo-600" /> 1. The Proposal
          </CardTitle>
          <CardDescription>Each existing debt rolled into the home loan at a single, lower rate.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Per-debt rows */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="proposal-debts-table">
              <thead>
                <tr className="border-b bg-slate-50">
                  <th className="text-left py-2 px-3 font-semibold text-gray-700">Debt</th>
                  <th className="text-right py-2 px-3 font-semibold text-gray-700">Balance</th>
                  <th className="text-right py-2 px-3 font-semibold text-gray-700">Current Rate</th>
                  <th className="text-right py-2 px-3 font-semibold text-gray-700">Current Monthly</th>
                  <th className="text-center py-2 px-3 font-semibold text-gray-700"></th>
                  <th className="text-right py-2 px-3 font-semibold text-indigo-700">Proposed Rate</th>
                </tr>
              </thead>
              <tbody>
                {debts.map((d, i) => (
                  <tr key={d.id || i} className="border-b hover:bg-slate-50">
                    <td className="py-2 px-3">
                      <div className="font-medium text-gray-900">{d.name || `Debt ${i + 1}`}</div>
                      <div className="text-xs text-gray-500">{d.term} yr term</div>
                    </td>
                    <td className="text-right py-2 px-3 text-gray-900">{formatCurrency(d.amount)}</td>
                    <td className="text-right py-2 px-3 text-rose-600 font-medium">{Number(d.rate).toFixed(2)}%</td>
                    <td className="text-right py-2 px-3">{formatCurrency(debtMonthly(d))}</td>
                    <td className="text-center py-2 px-3 text-indigo-500"><ArrowRight className="h-4 w-4 inline" /></td>
                    <td className="text-right py-2 px-3 text-emerald-600 font-semibold">{Number(consolidationRate).toFixed(2)}%</td>
                  </tr>
                ))}
                <tr className="border-b bg-slate-50">
                  <td className="py-2 px-3 font-medium text-gray-900">Existing Mortgage</td>
                  <td className="text-right py-2 px-3 text-gray-900">{formatCurrency(mortgage.remainingBalance)}</td>
                  <td className="text-right py-2 px-3 text-amber-700 font-medium">{Number(mortgage.interestRate).toFixed(2)}%</td>
                  <td className="text-right py-2 px-3">{formatCurrency(results.current.monthlyMortgagePayment)}</td>
                  <td className="text-center py-2 px-3 text-indigo-500"><ArrowRight className="h-4 w-4 inline" /></td>
                  <td className="text-right py-2 px-3 text-emerald-600 font-semibold">{Number(consolidationRate).toFixed(2)}%</td>
                </tr>
                <tr className="bg-emerald-50">
                  <td className="py-3 px-3 font-bold text-gray-900">Combined → New Mortgage</td>
                  <td className="text-right py-3 px-3 font-bold text-emerald-700">{formatCurrency(results.consolidated.newMortgageAmount)}</td>
                  <td className="text-right py-3 px-3 text-emerald-700 font-bold">{Number(consolidationRate).toFixed(2)}%</td>
                  <td className="text-right py-3 px-3 font-bold text-emerald-700">{formatCurrency(proposedMonthly)} /mo</td>
                  <td colSpan={2}></td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Plain-English explainer */}
          <div className="grid md:grid-cols-3 gap-4">
            <div className="bg-slate-50 rounded-lg p-4 border">
              <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">Step 1 — Refinance</div>
              <p className="text-sm text-gray-700">
                Roll <strong>{formatCurrency(results.current.totalDebtAmount)}</strong> of high-interest debt into your home loan, taking the new balance to <strong>{formatCurrency(results.consolidated.newMortgageAmount)}</strong>.
              </p>
            </div>
            <div className="bg-slate-50 rounded-lg p-4 border">
              <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">Step 2 — Single Rate</div>
              <p className="text-sm text-gray-700">
                Replace a weighted average rate of <strong className="text-rose-600">{results.current.averageRate.toFixed(2)}%</strong> with a single mortgage rate of <strong className="text-emerald-600">{Number(consolidationRate).toFixed(2)}%</strong>.
              </p>
            </div>
            <div className="bg-slate-50 rounded-lg p-4 border">
              <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">Step 3 — Redraw</div>
              <p className="text-sm text-gray-700">
                Park the <strong>{formatCurrency(monthlySavings)}/mo</strong> reduction into the redraw account so you stay on the same total spend and crush the balance faster.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ============== 2. SAVINGS COMPARISON ============== */}
      <Card className="border-2 border-emerald-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingDown className="h-5 w-5 text-emerald-600" /> 2. What You'll Reduce
          </CardTitle>
          <CardDescription>Side-by-side comparison: today vs. consolidated vs. consolidated + redraw.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Headline savings tiles */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="rounded-lg p-4 bg-gradient-to-br from-emerald-50 to-green-100 border-2 border-emerald-200">
              <div className="flex items-center gap-2 mb-1">
                <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                <span className="text-xs uppercase tracking-wide text-emerald-700 font-semibold">Monthly Saving</span>
              </div>
              <div className="text-3xl font-bold text-emerald-700">{formatCurrency(monthlySavings)}</div>
              <p className="text-xs text-gray-600 mt-1">
                {formatCurrency(currentMonthly)} → {formatCurrency(proposedMonthly)} ({((monthlySavings / currentMonthly) * 100).toFixed(1)}% lower)
              </p>
            </div>
            <div className="rounded-lg p-4 bg-gradient-to-br from-blue-50 to-sky-100 border-2 border-blue-200">
              <div className="flex items-center gap-2 mb-1">
                <CheckCircle2 className="h-4 w-4 text-blue-600" />
                <span className="text-xs uppercase tracking-wide text-blue-700 font-semibold">Lifetime Interest Reduction</span>
              </div>
              <div className="text-3xl font-bold text-blue-700">{formatCurrency(totalSavings)}</div>
              <p className="text-xs text-gray-600 mt-1">Consolidation + redraw strategy combined.</p>
            </div>
            <div className="rounded-lg p-4 bg-gradient-to-br from-purple-50 to-fuchsia-100 border-2 border-purple-200">
              <div className="flex items-center gap-2 mb-1">
                <CheckCircle2 className="h-4 w-4 text-purple-600" />
                <span className="text-xs uppercase tracking-wide text-purple-700 font-semibold">Mortgage Paid Off</span>
              </div>
              <div className="text-3xl font-bold text-purple-700">{yearsSaved} yrs sooner</div>
              <p className="text-xs text-gray-600 mt-1">{formatMonths(remainingMonths)} → {formatMonths(remainingMonths - results.redrawStrategy.monthsSaved)}</p>
            </div>
          </div>

          {/* Three-column scenario comparison */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm border" data-testid="savings-comparison-table">
              <thead>
                <tr className="bg-slate-100">
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Metric</th>
                  <th className="text-right py-3 px-4 font-semibold text-gray-700">Current</th>
                  <th className="text-right py-3 px-4 font-semibold text-indigo-700">Consolidated</th>
                  <th className="text-right py-3 px-4 font-semibold text-emerald-700">+ Redraw Strategy</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b">
                  <td className="py-3 px-4 font-medium">Monthly outlay</td>
                  <td className="text-right py-3 px-4">{formatCurrency(currentMonthly)}</td>
                  <td className="text-right py-3 px-4 text-indigo-700">
                    {formatCurrency(proposedMonthly)}
                    <div className="text-xs text-emerald-600">−{formatCurrency(monthlySavings)}</div>
                  </td>
                  <td className="text-right py-3 px-4 text-emerald-700">
                    {formatCurrency(redrawMonthly)}
                    <div className="text-xs text-gray-500">same spend, more principal</div>
                  </td>
                </tr>
                <tr className="border-b bg-slate-50/50">
                  <td className="py-3 px-4 font-medium">Annual outlay</td>
                  <td className="text-right py-3 px-4">{formatCurrency(currentMonthly * 12)}</td>
                  <td className="text-right py-3 px-4 text-indigo-700">
                    {formatCurrency(proposedMonthly * 12)}
                    <div className="text-xs text-emerald-600">−{formatCurrency(monthlySavings * 12)}/yr</div>
                  </td>
                  <td className="text-right py-3 px-4 text-emerald-700">{formatCurrency(redrawMonthly * 12)}</td>
                </tr>
                <tr className="border-b">
                  <td className="py-3 px-4 font-medium">Total interest paid</td>
                  <td className="text-right py-3 px-4">{formatCurrency(currentTotalDebtInterest + (results.current.monthlyMortgagePayment * remainingMonths - mortgage.remainingBalance))}</td>
                  <td className="text-right py-3 px-4 text-indigo-700">{formatCurrency(results.consolidated.totalInterest)}</td>
                  <td className="text-right py-3 px-4 text-emerald-700">
                    {formatCurrency(results.redrawStrategy.totalInterest)}
                    <div className="text-xs text-emerald-600">−{formatCurrency(totalSavings)} vs current</div>
                  </td>
                </tr>
                <tr className="border-b bg-slate-50/50">
                  <td className="py-3 px-4 font-medium">Average rate</td>
                  <td className="text-right py-3 px-4 text-rose-600">{results.current.averageRate.toFixed(2)}% (debts)</td>
                  <td className="text-right py-3 px-4 text-emerald-700">{Number(consolidationRate).toFixed(2)}%</td>
                  <td className="text-right py-3 px-4 text-emerald-700">{Number(consolidationRate).toFixed(2)}%</td>
                </tr>
                <tr>
                  <td className="py-3 px-4 font-medium">Mortgage payoff</td>
                  <td className="text-right py-3 px-4">—</td>
                  <td className="text-right py-3 px-4 text-indigo-700">{formatMonths(remainingMonths)}</td>
                  <td className="text-right py-3 px-4 text-emerald-700">
                    {formatMonths(remainingMonths - results.redrawStrategy.monthsSaved)}
                    <div className="text-xs text-emerald-600">{formatMonths(results.redrawStrategy.monthsSaved)} earlier</div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Bottom-line summary banner */}
          <div className="rounded-lg p-5 bg-gradient-to-r from-emerald-600 to-teal-600 text-white">
            <div className="grid md:grid-cols-3 gap-4 text-center">
              <div>
                <div className="text-xs uppercase tracking-wide opacity-80">Cash freed up monthly</div>
                <div className="text-2xl font-bold">{formatCurrency(monthlySavings)}</div>
              </div>
              <div className="border-l border-r border-white/30">
                <div className="text-xs uppercase tracking-wide opacity-80">Total interest reduction</div>
                <div className="text-2xl font-bold">{formatCurrency(totalSavings)}</div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wide opacity-80">Mortgage-free</div>
                <div className="text-2xl font-bold">{yearsSaved} yrs early</div>
              </div>
            </div>
          </div>

          {/* Disclaimer */}
          <div className="flex items-start gap-2 text-xs text-gray-500 p-3 rounded-md bg-amber-50 border border-amber-200">
            <AlertTriangle className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />
            <p>
              Please do not rely on this report as financial or credit advice, as the figures and information provided are estimates only. This information is general in nature and should be considered in conjunction with the NCCP requirements, your full application details, and the formal loan and credit documentation provided by the lender should you decide to proceed further.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ProposalBreakdown;
